import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import type { TelegramConfig } from '../types';

type Status = { kind: 'ok' | 'error'; text: string } | null;

const Settings: React.FC = () => {
  const [config, setConfig] = useState<TelegramConfig>({
    bot_configured: false,
    chat_id: '',
    notifications_enabled: false,
    notification_threshold: 1,
    traffic_alert_enabled: true,
    traffic_alert_window_minutes: 20,
    stuck_alert_enabled: true,
    stuck_alert_min_denials: 3,
  });
  // Kept apart from `config`: the API never returns the stored token, so this
  // field is write-only and stays blank unless the user is replacing it.
  const [botToken, setBotToken] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [status, setStatus] = useState<Status>(null);

  useEffect(() => {
    fetchTelegramConfig();
  }, []);

  const fetchTelegramConfig = async () => {
    try {
      setLoading(true);
      setConfig(await apiService.getTelegramConfig());
    } catch (error) {
      setStatus({ kind: 'error', text: `Ne mogu da učitam podešavanja: ${(error as Error).message}` });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setStatus(null);
      const saved = await apiService.updateTelegramConfig({
        // Sending an empty token would be a no-op server-side, but skipping it
        // keeps the intent explicit.
        ...(botToken ? { bot_token: botToken } : {}),
        chat_id: config.chat_id,
        notifications_enabled: config.notifications_enabled,
        notification_threshold: config.notification_threshold,
        traffic_alert_enabled: config.traffic_alert_enabled,
        traffic_alert_window_minutes: config.traffic_alert_window_minutes,
        stuck_alert_enabled: config.stuck_alert_enabled,
        stuck_alert_min_denials: config.stuck_alert_min_denials,
      });
      setConfig(saved);
      setBotToken('');
      setStatus({ kind: 'ok', text: 'Podešavanja su sačuvana.' });
    } catch (error) {
      setStatus({ kind: 'error', text: `Čuvanje nije uspelo: ${(error as Error).message}` });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    try {
      setTesting(true);
      setStatus(null);
      const result = await apiService.testTelegramConfig();
      setStatus({ kind: result.success ? 'ok' : 'error', text: result.detail });
    } catch (error) {
      setStatus({ kind: 'error', text: `Test nije uspeo: ${(error as Error).message}` });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      <h1 className="text-2xl font-semibold text-gray-900">Settings</h1>
      <p className="mt-2 text-sm text-gray-700">
        Configure platform settings and integrations.
      </p>

      <div className="mt-6 bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Telegram Notifications
          </h3>
          <p className="mt-1 text-sm text-gray-600">
            Configure the Telegram bot that reports when the HLR or HSS side stops
            sending traffic.
          </p>

          <div className="mt-6 grid grid-cols-1 gap-6">
            <div>
              <label htmlFor="bot_token" className="block text-sm font-medium text-gray-700">
                Bot Token
              </label>
              <input
                type="password"
                name="bot_token"
                id="bot_token"
                value={botToken}
                onChange={(e) => setBotToken(e.target.value)}
                className="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                placeholder={config.bot_configured ? 'Token je sačuvan — unesite novi da ga zamenite' : 'Enter your Telegram bot token'}
                autoComplete="off"
              />
              <p className="mt-1 text-xs text-gray-500">
                {config.bot_configured
                  ? 'Postojeći token se ne prikazuje. Ostavite polje prazno da ga zadržite.'
                  : 'Token još nije podešen.'}
              </p>
            </div>

            <div>
              <label htmlFor="chat_id" className="block text-sm font-medium text-gray-700">
                Chat ID
              </label>
              <input
                type="text"
                name="chat_id"
                id="chat_id"
                value={config.chat_id}
                onChange={(e) => setConfig({ ...config, chat_id: e.target.value })}
                className="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                placeholder="Enter your Telegram chat ID"
              />
            </div>

            <div>
              <label htmlFor="traffic_alert_window_minutes" className="block text-sm font-medium text-gray-700">
                Traffic Alert Window (minutes)
              </label>
              <input
                type="number"
                min={5}
                max={1440}
                name="traffic_alert_window_minutes"
                id="traffic_alert_window_minutes"
                value={config.traffic_alert_window_minutes}
                onChange={(e) =>
                  setConfig({
                    ...config,
                    traffic_alert_window_minutes: Math.min(1440, Math.max(5, Number(e.target.value) || 20)),
                  })
                }
                className="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
              />
              <p className="mt-1 text-xs text-gray-500">
                Alert se šalje ako u ovom prozoru ne stigne <strong>nijedan</strong> HLR
                ili nijedan HSS upit. HLR i HSS se prate odvojeno. Jedna poruka po
                prekidu, pa poruka kad se saobraćaj vrati. Najmanje 5 minuta.
              </p>
            </div>

            <div>
              <label htmlFor="stuck_alert_min_denials" className="block text-sm font-medium text-gray-700">
                Stuck Subscriber Threshold
              </label>
              <input
                type="number"
                min={3}
                max={100}
                name="stuck_alert_min_denials"
                id="stuck_alert_min_denials"
                value={config.stuck_alert_min_denials}
                onChange={(e) =>
                  setConfig({
                    ...config,
                    stuck_alert_min_denials: Math.min(100, Math.max(3, Number(e.target.value) || 3)),
                  })
                }
                className="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
              />
              <p className="mt-1 text-xs text-gray-500">
                Kad procentualna raspodela odbije istog pretplatnika ovoliko puta u
                prozoru iznad, a on nijednom ne prođe, šalje se alarm — verovatno nema
                pokrivenost operatera kome je dodeljen. Najmanje 3.
              </p>
            </div>

            <div className="flex items-center">
              <input
                id="stuck_alert_enabled"
                name="stuck_alert_enabled"
                type="checkbox"
                checked={config.stuck_alert_enabled}
                onChange={(e) =>
                  setConfig({ ...config, stuck_alert_enabled: e.target.checked })
                }
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <label htmlFor="stuck_alert_enabled" className="ml-2 block text-sm text-gray-900">
                Alert when steering leaves a subscriber with no network
              </label>
            </div>

            <div className="flex items-center">
              <input
                id="traffic_alert_enabled"
                name="traffic_alert_enabled"
                type="checkbox"
                checked={config.traffic_alert_enabled}
                onChange={(e) =>
                  setConfig({ ...config, traffic_alert_enabled: e.target.checked })
                }
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <label htmlFor="traffic_alert_enabled" className="ml-2 block text-sm text-gray-900">
                Alert when an interface goes silent
              </label>
            </div>

            <div className="flex items-center">
              <input
                id="notifications_enabled"
                name="notifications_enabled"
                type="checkbox"
                checked={config.notifications_enabled}
                onChange={(e) =>
                  setConfig({ ...config, notifications_enabled: e.target.checked })
                }
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <label
                htmlFor="notifications_enabled"
                className="ml-2 block text-sm text-gray-900"
              >
                Enable notifications
              </label>
            </div>
          </div>

          {status && (
            <div
              className={`mt-6 rounded-md p-3 text-sm ${
                status.kind === 'ok'
                  ? 'bg-green-50 text-green-800'
                  : 'bg-red-50 text-red-800'
              }`}
            >
              {status.text}
            </div>
          )}
        </div>

        <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
          <button
            type="button"
            onClick={handleTest}
            disabled={testing || !config.bot_configured}
            title={config.bot_configured ? undefined : 'Sačuvajte bot token pre testiranja'}
            className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
          >
            {testing ? 'Testing...' : 'Test'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Settings;
